import * as d3 from 'd3'
import { Constants } from "../constants/Variables";
import { exportComponentAsPNG } from 'react-component-export-image'; // responsible for PNG download
import html2canvas from 'html2canvas'; // responsible for PDF download
import jsPDF from 'jspdf'; // responsible for PDF download
import { data as statemap_json_before_2020 } from "../data/states_2019";
import { data as statemap_json_after_2020 } from "../data/states_2020";
import { data as districtmap_json } from "../data/districts_2011";

/**
 * Formats large numbers for y-axis display
 * Converts 'G' (Giga) to 'B' (Billion) in d3 formatted output
 * @param {number} number - The number to format
 * @returns {string} Formatted number string
 */
const yAxisTickFormat = number =>
  d3.format('.2s')(number)
    .replace('G', 'B');

/**
 * Determines the boundary year for Telangana data based on source ID
 * @param {number} searchid - Source identifier
 * @returns {number} Boundary year
*/
export const boundary_year = (searchid, selectedYear) => {
  //function checks the sfid and returns the year after which telangana starts to appear in the source data
    //eg. for nss: 2014, asi: 2012, ncrb: 2013
    if (searchid >= 977 && searchid <= 994) {
      //asi
      return 2012;
    } else if (searchid === 581 || searchid === 582) {
      //ncrb
      return 2013;
    } else if (searchid >= 999 && searchid <= 1009) {
      //nss/plfs
      return 2014;
    } else if (searchid >= 594 && searchid <= 973) {
      //nfhs
      return 2014;
    } else if (searchid >= 995 && searchid <= 997) {
      //rbi
      return 2014;
    } else if (searchid === 998) {
      //rbi
      return 2014;
    } else {
      return selectedYear
    }
}

export const sourceYearWiseMap = async (source, year, view) => {
  try {
  var tempData=null;
  const numYear = parseInt(year);
  if (numYear >= 2021) {
    // Merged Daman and Dadra, Split J&K and Ladakh, Split AP and Telangana
    tempData = await JSON.parse(JSON.stringify(statemap_json_after_2020));
    tempData.features.splice(28, 2);
    tempData.features.splice(18, 1);
  } else if (numYear >= 2020) {
    // Split J&K and Ladakh, Split AP and Telangana
    tempData = await JSON.parse(JSON.stringify(statemap_json_after_2020));
    tempData.features.splice(27, 1);
    tempData.features.splice(18, 1);
  } else if (numYear >= 2014) {
    // Split AP and Telangana
    tempData = await JSON.parse(
      JSON.stringify(statemap_json_before_2020)
    );
    tempData.features.splice(11, 1);
  } else {
    // Just AP
    tempData = await JSON.parse(
      JSON.stringify(statemap_json_before_2020)
    );
    tempData.features.splice(0, 2);
  }
  if (!tempData || !tempData.features) {
    console.error("Invalid map data generated for year:", year, "source:", source, "view:", view);
    // Fallback to a default map if needed
    return view === Constants.State ? statemap_json_after_2020 : districtmap_json;
  }
  return tempData;
}catch (error) {
  console.error("Error in sourceYearWiseMap:", error);
  // Return a default map in case of error
  return view === Constants.State ? statemap_json_after_2020 : districtmap_json;
}
}

export const checkYearWiseStateValidity = (year, stc, visualization=Constants.Map) => {
  if(parseInt(stc)===36){
    if(parseInt(year)>=2014) return true;
    else return false;
  }
  else if(parseInt(stc)===38){
    if(parseInt(year)>=2020) return true;
    else return false;
  }
  else if(parseInt(stc)===50 ){
    if(parseInt(year)>=2021) return true;
    else return false;
  }
  else if((parseInt(stc)===25 || parseInt(stc)===26)){
    if(visualization===Constants.Line)  return true;
    else{
      if(parseInt(year)<2021) return true;
      else return false;
    }
  }
  else if(parseInt(stc)===1){ // J&K
    return true; // Always show J&K
  }
  else if(parseInt(stc)===28){ // Andhra Pradesh
    return true; // Always show AP
  }
  return true;
}

/**
 * Formats numbers for tick display with special handling for large numbers
 * @param {number} num - Number to format
 * @returns {string} Formatted number or "NA" for invalid numbers
 */
export const tickFormatting = (num) => {
  var num_format, temp;
  if (num > 1000) {
    temp = yAxisTickFormat(num);
    if (isNaN(num)) {
      return "NA";
    } else {
      return temp;
    }
  } else {
    num_format = d3.format(".2f");
    if (isNaN(num)) {
      return "NA";
    } else {
      return num_format(num);
    }
  }
}

/**
 * Formats numbers specifically for heatmap visualization
 * @param {number} num - Number to format
 * @returns {string} Formatted number or "Value not provided" for invalid numbers
 */
export const heatmapTickFormatting = (num) => {
  var num_format;
  num_format = d3.format(".2s");
  if (isNaN(num)) {
    return "Value not provided";
  } else {
    return num_format(num);
  }
}

/**
 * Generates and downloads CSV file from visualization data
 * @param {Array} dataSet - Dataset for visualization
 * @param {Array} indiaData - India-level data
 * @param {Object} statenames - Mapping of state codes to names
 * @param {Object} districtnames - Mapping of district codes to names
 * @param {Object} districtStateNames - Mapping of districts to their states
 * @param {string} view - Current view mode (State/District)
 * @param {string} visualization - Type of visualization (Bar/Line/Map)
 * @param {Array} sliderRange - Selected year range
 * @param {string} selectedStateCode - Selected state code
 * @param {string} fileName - Output file name
 * @param {Array} allYears - All available years
 */
export const csvDownload = async (
  dataSet,
  indiaData,
  statenames,
  districtnames,
  districtStateNames,
  view,
  visualization,
  sliderRange,
  selectedStateCode,
  fileName="abcd_csvfile",
  allYears
) => {
  // Create a copy of the dataset to work with
  let filteredDataSet = [...dataSet];
  
  // For Bar charts or Map visualizations
  if(visualization===Constants.Bar || visualization===Constants.Map){
    // Start building CSV content with header row
    var csvContent = `States,${sliderRange[0]}\n`;
    
    // If viewing at state level, add India total
    if(view===Constants.State){
      csvContent += "India,";
      csvContent += parseFloat(indiaData[0].value).toFixed(2); // Format to 2 decimal places
      csvContent += "\n";
    }
    // If viewing at district level, add state total
    else{
      csvContent += `${statenames[indiaData[0]["stc"]]},`;
      csvContent += parseFloat(indiaData[0].value).toFixed(2);
      csvContent += "\n";
    }
    
    // Add data for each state or district to the CSV
    filteredDataSet.forEach(item => {
      if(selectedStateCode==="")  csvContent += statenames[item.stc] + ","; // Add state name
      else csvContent += `${districtnames[item.dtc]} (${statenames[item.stc]}),`; // Add district with state name
      csvContent += parseFloat(item.value).toFixed(2); // Add value with 2 decimals
      csvContent += "\n";
    });
  }

  // For Line chart visualizations
  if(visualization===Constants.Line){
    // Filter data based on selected state (if any) and year range
    if(selectedStateCode){
      filteredDataSet = await filteredDataSet.filter(item => {
        let year = new Date(item.date?.concat(' 00:00:00')).getFullYear(); // Extract year from date
        return (
          sliderRange[0] <= year && year <= sliderRange[1] && item.stc===parseInt(selectedStateCode)
        );
      });
    }
    else{
      filteredDataSet = await filteredDataSet.filter(item => {
        let year = new Date(item.date?.concat(' 00:00:00')).getFullYear();
        return (
          sliderRange[0] <= year && year <= sliderRange[1]
        );
      });
    }

    // Create header row with years
    csvContent = (view === Constants.State ? "States," : "Districts,");
    for(let i=0;i<allYears.length;i++){
      if(allYears[i]>=sliderRange[0] && allYears[i]<=sliderRange[1]){
        csvContent += allYears[i];
        if(i<allYears.length-1) csvContent += ","
      }
    }
    csvContent += "\n";
    
    // For state-level view, add India data and state data
    if(view === Constants.State){
      // Add India total row
      csvContent += "India,";
      for(let i=0;i<allYears.length;i++){
        if(allYears[i]>=sliderRange[0] && allYears[i]<=sliderRange[1]){
          // Find data for each year
          const foundObject = indiaData.find(
            obj => obj["date"] === `${allYears[i]}-01-01`
          );
          if(foundObject){
            csvContent += parseFloat(foundObject.value).toFixed(2);
          }
          else{
            csvContent +=  "Not Found"; // If data not available for year
          }
          csvContent += ",";
        }
      }
      csvContent += "\n";
      
      // Add data for each state across years
      Object.keys(statenames).forEach((key, idx) => {
        if(key!=='0'){
          csvContent += statenames[key] + ",";
          for(let i=0;i<allYears.length;i++){
            if(allYears[i]>=sliderRange[0] && allYears[i]<=sliderRange[1]){
              // Find data for this state and year
              const foundObject = filteredDataSet.find(
                obj => obj["stc"] === parseInt(key) && obj["date"] === `${allYears[i]}-01-01`
              );
              if(foundObject){
                csvContent += parseFloat(foundObject.value).toFixed(2);
              }
              else{
                csvContent +=  "Not Found";
              }
              if(i < allYears.length-1) csvContent += ",";
            }
          }
          csvContent += "\n";
        }})
    }
    // For district-level view, add state total and district data
    else{
      // Add state total row
      csvContent += `${statenames[indiaData[0]["stc"]]},`;
      for(let i=0;i<allYears.length;i++){
        if(allYears[i]>=sliderRange[0] && allYears[i]<=sliderRange[1]){
          const foundObject = indiaData.find(
            obj => obj["date"] === `${allYears[i]}-01-01`
          );
          if(foundObject){
            csvContent += parseFloat(foundObject.value).toFixed(2);
          }
          else{
            csvContent +=  "Not Found";
          }
          csvContent += ",";
        }
      }
      csvContent += "\n";
      
      // Add data for each district across years
      Object.keys(districtnames).forEach((key, idx) => {
        if(key!=='0'){
          csvContent += `${districtnames[key]} (${districtStateNames[key]}),`;
          for(let i=0;i<allYears.length;i++){
            // Find data for this district and year
            const foundObject = filteredDataSet.find(
              obj => obj["dtc"] === parseInt(key) && obj["date"] === `${allYears[i]}-01-01`
            );
            if(foundObject){
              csvContent += parseFloat(foundObject.value).toFixed(2);
            }
            else{
              csvContent +=  "Not Found";
            }
            if(i < allYears.length-1) csvContent += ",";
          }
          csvContent += "\n";
      }})
    }
  }

  // Create hidden link element to trigger download
  var hiddenElement = document.createElement("a");
  hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csvContent);
  hiddenElement.target = "_blank";

  // Set download filename and trigger download
  hiddenElement.download = fileName;
  hiddenElement.click();
};

/**
 * Exports visualization as PNG image with custom styling
 * @param {React.RefObject} componentRef - Reference to component to export
 * @param {string} fileName - Output file name
 * @param {boolean} compareFlag - Whether in comparison mode
 * @param {string} visualization - Type of visualization
 */
export const downloadBarLinePNG = async (componentRef, fileName, compareFlag, visualization) => {
  // Export the chart as PNG image with custom styling
  await exportComponentAsPNG(componentRef, {
    fileName: fileName,
    html2CanvasOptions: {
      onclone: (clonedDoc) => {
        // Adjust styling for image export
        if (!compareFlag) clonedDoc.getElementById("chartSectionHeader-wrapper").style.marginLeft = "1rem";
        clonedDoc.getElementById("mainContentContainer-mid").style.marginLeft = "1rem";
        clonedDoc.getElementById("charttooltips").style.display = "none"; // Hide tooltips
        clonedDoc.getElementById("visualization-wrapper").style.width = `${clonedDoc.getElementById("visualization-wrapper").offsetWidth + 100}px`;
        
        // For comparison view
        if (compareFlag) clonedDoc.getElementById("visualization-compare-wrapper").style.width = `${clonedDoc.getElementById("visualization-wrapper").offsetWidth + 100}px`;
        
        // Hide unnecessary elements
        clonedDoc.getElementById("heading-icons-wrapper").style.display = "none";
        if (!compareFlag) clonedDoc.getElementById("rightContainer").style.display = "none";
        if (compareFlag) clonedDoc.getElementById("edit-filter-btn-wrapper-compare").style.display = "none";
        if (compareFlag) clonedDoc.getElementById("heading-icons-wrapper-compare").style.display = "none";
        
        // Show logo and position it correctly
        clonedDoc.getElementById("download-ceda-logo").style.visibility = "visible";
        clonedDoc.getElementById("download-ceda-logo").style.marginLeft = "1rem";
        if (visualization === Constants.Bar) {
          clonedDoc.getElementById("download-ceda-logo").style.top = "37rem";
        }
        clonedDoc.getElementById("download-ceda-logo").style.left = "16.5rem";
        
        // Special styling for line charts
        if (visualization === Constants.Line) {
          clonedDoc.getElementById("download-ceda-logo").style.top = "40.5rem";
          clonedDoc.getElementById("line-chart-legend").style.display = "inline";
        }
      },
    },
  });
}

/**
 * Exports visualization as PDF document
 * @param {React.RefObject} componentRef - Reference to component to export
 * @param {boolean} compareFlag - Whether in comparison mode
 * @param {string} visualization - Type of visualization
 * @param {Object} selectedSection - Selected data section containing variable name
 */
export const downloadBarLinePDF = async (componentRef, compareFlag, visualization, selectedSection) => {
  // Convert the chart component to a canvas element
  const canvas = await html2canvas(componentRef.current, {
    onclone: (clonedDoc) => {
      // Same styling adjustments as PNG export
      if (!compareFlag) clonedDoc.getElementById("chartSectionHeader-wrapper").style.marginLeft = "1rem";
      clonedDoc.getElementById("mainContentContainer-mid").style.marginLeft = "1rem";
      clonedDoc.getElementById("charttooltips").style.display = "none";
      clonedDoc.getElementById("visualization-wrapper").style.width = `${clonedDoc.getElementById("visualization-wrapper").offsetWidth + 100}px`;
      if (compareFlag) clonedDoc.getElementById("visualization-compare-wrapper").style.width = `${clonedDoc.getElementById("visualization-wrapper").offsetWidth + 100}px`;
      clonedDoc.getElementById("heading-icons-wrapper").style.display = "none";
      if (!compareFlag) clonedDoc.getElementById("rightContainer").style.display = "none";
      if (compareFlag) clonedDoc.getElementById("edit-filter-btn-wrapper-compare").style.display = "none";
      if (compareFlag) clonedDoc.getElementById("heading-icons-wrapper-compare").style.display = "none";
      clonedDoc.getElementById("download-ceda-logo").style.visibility = "visible";
      clonedDoc.getElementById("download-ceda-logo").style.marginLeft = "1rem";
      if (visualization === Constants.Bar) {
        clonedDoc.getElementById("download-ceda-logo").style.top = "37rem";
      }
      clonedDoc.getElementById("download-ceda-logo").style.left = "16.5rem";
      if (visualization === Constants.Line) {
        clonedDoc.getElementById("download-ceda-logo").style.top = "40.5rem";
        clonedDoc.getElementById("line-chart-legend").style.display = "inline";
      }
    },
  });
  
  // If canvas was created successfully
  if (canvas) {
    // Convert canvas to image data
    const imgData = canvas.toDataURL('image/png');
    
    // Create new PDF document (portrait, millimeters, A4 size)
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    // Add the image to PDF and save with name based on variable name
    pdf.addImage(imgData, 'PNG', 0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight() - 180);
    pdf.save(selectedSection["variable_name"].split(" ").join("_"));
  } else {
    console.error("Canvas generation failed");
  }
}